/** @jsx jsx */
import { Container, Flex } from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { omit } from "lodash-es";
import { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "router";
import { jsx } from "theme-ui";

import { ConsultantProfileContext } from "@/components/User";
import { ListViewHeader } from "@/components/views";
import { IURLSortConfig } from "@/core/url";
import {
  consultantProfileListFiltersMapper,
  consultantProfileListSearchParamsType,
  fetchConsultantProfileList,
  IConsultantProfileURLFilter,
  IPageableShortConsultantProfile,
  IShortConsultantProfile,
} from "@/data/consultant-profiles";
import { TPageableFetchFunction, useMetaPageableFetcher } from "@/hooks/http";
import { useDebounce } from "@/hooks/Timeout";
import { mapToQuery } from "@/infrastructure/Filter/service";
import { TeamFilters } from "@/views-new/companies/filters/TeamFilters";
import { useTeamFilters } from "@/views-new/companies/filters/useTeamFilters";
import { InviteButton } from "@/views-new/invitations";

import { TeamList } from "./TeamList";

interface TeamPageProps {}

export const TeamPage: FC<TeamPageProps> = ({}) => {
  const { currentProfileId, currentCompanyId } = useContext(ConsultantProfileContext);
  const { t } = useTranslation("session");
  const breakpoint = useBreakpointIndex();

  const { searchParams: meta, replaceSearchParams: changeMeta } = useRouter({
    ensureSearchParams: consultantProfileListSearchParamsType,
  });

  const debounceTime = 600;

  const fetchData: TPageableFetchFunction<
    IShortConsultantProfile,
    IPageableShortConsultantProfile,
    IConsultantProfileURLFilter,
    IURLSortConfig[]
  > = useCallback(
    (params, options) => {
      const filters = params.filtersConfig ?? {};

      return fetchConsultantProfileList(
        {
          ...omit(params, "filtersConfig"),
          companyId: currentCompanyId,
          ...mapToQuery(consultantProfileListFiltersMapper, filters),
        },
        options,
      );
    },
    [currentCompanyId, currentProfileId],
  );

  const debouncedFilters = useDebounce(meta.filtersConfig, debounceTime);

  const mappedMeta = useMemo(() => {
    return {
      ...meta,
      filtersConfig: debouncedFilters,
    };
  }, [meta, debouncedFilters]);

  const { working, payload, changeFilter, changePage, changePageSize, fetch, changeSort, updateRecord } =
    useMetaPageableFetcher(
      fetchData,
      {
        meta: mappedMeta,
        changeMeta,
      },
      [JSON.stringify(mappedMeta), currentCompanyId],
    );

  const { handleFiltersChange, clearFilters } = useTeamFilters(meta.filtersConfig, changeFilter);

  return (
    <Flex flexDirection="column">
      <ListViewHeader
        header={t("COMPANY.TEAM.HEADER")}
        count={payload?.totalElements}
        addButton={<InviteButton>{breakpoint >= 2 && t("COMPANY.TEAM.ADD_USER_BUTTON")}</InviteButton>}>
        <Flex gap="xs">
          <TeamFilters
            onFiltersUpdate={handleFiltersChange}
            onFilterClear={clearFilters}
            filtersValue={meta.filtersConfig}
          />
        </Flex>
      </ListViewHeader>

      <Container>
        <Flex alignItems="center" justifyContent="center" pt="md" px="sm" sx={{ paddingBottom: "80px" }}>
          <TeamList
            payload={payload}
            working={working}
            changePage={changePage}
            fetch={fetch}
            changePageSize={changePageSize}
            changeFilter={changeFilter}
            changeSort={changeSort}
            counter={0}
            updateRecord={updateRecord}
          />
        </Flex>
      </Container>
    </Flex>
  );
};
